// @flow
import React from 'react';
import { type Option } from 'fp-ts/lib/Option';
import { option } from 'fp-ts';
import { formatPrice } from '../../helpers/currency';

import './ProductPrice.css';

export type PriceRule = {
  product_id: number,
  rule_id: number,
  action_operator: 'by_fixed'|'by_percent',
  action_amount: string
};

export type PriceObject = {
  productPrice: number,
  basePrice: number,
  productPriceAdd: number,
  envelopePrice: number,
  quantity: number,
  specialPrice: number,
  isCard: boolean,
  isConfigurable: boolean,
  isStandalone: boolean,
  displayFrom: boolean,
  catalogPriceRules: PriceRule[],
  possibleProductIds: string[],
};

const applyPriceRules = (possibleProductIds: string[], priceRules: PriceRule[], price: number) => priceRules
  .filter(pr => possibleProductIds.includes(pr.product_id))
  // Dedupe the rules
  .reduce((rules, current) => (
    rules.filter(r => r.rule_id === current.rule_id).length > 0 ?
      rules :
      rules.push(current) ? rules : rules
  ), [])
  // Apply the rules
  .reduce((total, currentRule) => {
    switch (currentRule.action_operator) {
      case 'by_fixed':
        return total - parseFloat(currentRule.action_amount);
      case 'by_percent': {
        const discount = total * (1 / parseFloat(currentRule.action_amount));
        return Math.ceil((total - discount) * 100) / 100;
      }
      default:
        break;
    }
    return price;
  }, price);

export const getSubtotal = (price: PriceObject) => {
  const totalPrice = price.productPrice + price.productPriceAdd + price.envelopePrice;
  let specialPrice = false;
  if (price.catalogPriceRules.length > 0) {
    specialPrice = applyPriceRules(price.possibleProductIds, price.catalogPriceRules, totalPrice);
  }
  return formatPrice(2)((specialPrice || totalPrice) * price.quantity);
};

export const getDynamicPricing = (price: PriceObject) => {
  let isSpecial = price.specialPrice > 0;
  const totalPrice = price.productPrice + price.productPriceAdd;
  let { specialPrice } = price;
  if (option.fromNullable(price.catalogPriceRules).getOrElseValue([]).length > 0) {
    isSpecial = true;
    specialPrice = applyPriceRules(price.possibleProductIds, price.catalogPriceRules, totalPrice);
  }

  return (
    <span>
      {price.displayFrom ? 'From ' : ''}

      {isSpecial ?
        <span className="crossedOutPrice">
          {formatPrice(2)(totalPrice)}
        </span> : ''
      }

      <span className={isSpecial > 0 ? 'specialPrice' : ''}>
        {formatPrice(2)(isSpecial ? specialPrice : totalPrice)}
      </span>

      {price.isCard ? ' per card ' : ' '}

      {
        price.envelopePrice > 0 ?
          `+ ${formatPrice(2)(price.envelopePrice)} per envelope ` :
          ''
      }

      {
        price.isCard && price.quantity > 1 ?
          ` (${getSubtotal(price)} subtotal)` :
          ''
      }
    </span>
  );
};

const ProductPrice = ({
  price, inStock,
}: {
  price: PriceObject, inStock: Option<boolean>
}) => (
  <p className="txt-bdy-1 -pb-tiny">
    {getDynamicPricing(price)}
    {
      inStock
      .map(() => <span className="error-text">This product is currently out of stock.</span>)
      .getOrElseValue(null)
    }
  </p>
);

export default ProductPrice;



// WEBPACK FOOTER //
// ./src/components/ProductPrice/index.js